import React, { useState, useEffect } from 'react';
import { LogOut, Users, FileText, Package, TrendingUp, Bell, Key, ChevronDown, X, Loader2, CheckCircle, AlertCircle, BookOpen, PenTool, Bot } from 'lucide-react';
import { signOut, changePassword } from '../../services/authService';
import { AdminUser } from '../../types';
import RetailerRequestsTab from './RetailerRequestsTab';
import RetailersTab from './RetailersTab';
import OrdersTab from './OrdersTab';
import SalesOverviewTab from './SalesOverviewTab';
import RetailerDirectoryTab from './RetailerDirectoryTab';
import ContentApprovalTab from './ContentApprovalTab';
import AIStudioTab from './AIStudioTab';
import { getPendingRequestCount } from '../../services/retailerService';
import { getPendingOrderCount } from '../../services/orderService';
import { getPendingContentCount } from '../../services/contentApprovalService';

interface AdminDashboardProps {
  admin: AdminUser;
  onLogout: () => void;
}

type TabType = 'requests' | 'retailers' | 'orders' | 'sales' | 'directory' | 'content' | 'studio';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ admin, onLogout }) => {
  const [activeTab, setActiveTab] = useState<TabType>('requests');
  const [pendingRequests, setPendingRequests] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [pendingContent, setPendingContent] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    loadCounts();
  }, []);

  useEffect(() => {
    if (!showPasswordModal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isChangingPassword) {
        setShowPasswordModal(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showPasswordModal, isChangingPassword]);

  const loadCounts = async () => {
    try {
      const [reqCount, orderCount, contentCount] = await Promise.all([
        getPendingRequestCount(),
        getPendingOrderCount(),
        getPendingContentCount()
      ]);
      setPendingRequests(reqCount);
      setPendingOrders(orderCount);
      setPendingContent(contentCount);
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(newPassword);
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
        setNewPassword('');
        setConfirmPassword('');
      }, 2000);
    } catch (err: any) {
      if (err.code === 'auth/requires-recent-login') {
        setPasswordError('For security, please log out and log back in before changing your password.');
      } else {
        setPasswordError(err.message || 'Failed to change password.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'requests', label: 'Requests', icon: <FileText className="w-4 h-4" strokeWidth={1.5} />, badge: pendingRequests },
    { id: 'retailers', label: 'Retailers', icon: <Users className="w-4 h-4" strokeWidth={1.5} /> },
    { id: 'orders', label: 'Orders', icon: <Package className="w-4 h-4" strokeWidth={1.5} />, badge: pendingOrders },
    { id: 'sales', label: 'Sales', icon: <TrendingUp className="w-4 h-4" strokeWidth={1.5} /> },
    { id: 'directory', label: 'Directory', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} /> },
    { id: 'content', label: 'Content', icon: <PenTool className="w-4 h-4" strokeWidth={1.5} />, badge: pendingContent },
    { id: 'studio', label: 'AI Studio', icon: <Bot className="w-4 h-4" strokeWidth={1.5} /> }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'requests':
        return <RetailerRequestsTab onUpdate={loadCounts} />;
      case 'retailers':
        return <RetailersTab adminEmail={admin.email} />;
      case 'orders':
        return <OrdersTab onUpdate={loadCounts} />;
      case 'sales':
        return <SalesOverviewTab />;
      case 'directory':
        return <RetailerDirectoryTab adminEmail={admin.email} />;
      case 'content':
        return <ContentApprovalTab adminEmail={admin.email} onUpdate={loadCounts} />;
      case 'studio':
        return <AIStudioTab adminEmail={admin.email} />;
      default:
        return null;
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <header className="bg-forge text-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-ember flex items-center justify-center">
                <span className="font-mono font-medium text-lg text-paper-warm">M</span>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ember-soft">— The Machete Man</p>
                <h1 className="font-editorial italic text-lg text-paper-warm tracking-tight leading-tight">Admin hub</h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Notifications indicator */}
              {(pendingRequests > 0 || pendingOrders > 0 || pendingContent > 0) && (
                <div className="relative">
                  <Bell className="w-5 h-5 text-paper/70" strokeWidth={1.5} />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-ember rounded-full flex items-center justify-center text-[10px] font-mono text-paper-warm">
                    {pendingRequests + pendingOrders + pendingContent}
                  </span>
                </div>
              )}

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 hover:bg-paper/10 px-3 py-2 transition-colors duration-150"
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-paper-warm">{admin.displayName}</p>
                    <p className="font-mono text-[10px] tracking-micro uppercase text-paper/60 capitalize">{admin.role.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-paper/60" strokeWidth={1.5} />
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-52 bg-paper-bright shadow-float border border-ink-200 py-1 z-20">
                      <button
                        onClick={() => {
                          setShowPasswordModal(true);
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-ink-700 hover:bg-paper hover:text-ink-950 flex items-center gap-2.5 transition-colors duration-150"
                      >
                        <Key className="w-4 h-4" strokeWidth={1.5} />
                        Change password
                      </button>
                      <hr className="my-1 border-ink-200" />
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2.5 text-left text-sm text-ember hover:bg-ember-soft/25 flex items-center gap-2.5 transition-colors duration-150"
                      >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-paper-bright border-b border-ink-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors duration-150 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-ember text-ember'
                    : 'border-transparent text-ink-500 hover:text-ink-700'
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="bg-ember text-paper-warm text-[10px] font-mono px-1.5 py-0.5 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {renderTabContent()}
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm"
            onClick={() => !isChangingPassword && setShowPasswordModal(false)}
          />
          <div className="relative bg-paper-bright shadow-float w-full max-w-md overflow-hidden">
            <div className="bg-forge text-paper px-6 py-5 relative">
              <button
                onClick={() => !isChangingPassword && setShowPasswordModal(false)}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-2">— Security</p>
              <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
                Change password.
              </h2>
            </div>

            {passwordSuccess ? (
              <div className="p-6 lg:p-8 text-center">
                <div className="w-14 h-14 bg-olive/15 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-6 h-6 text-olive" strokeWidth={1.5} />
                </div>
                <h3 className="font-editorial italic text-xl text-ink-950 mb-2">Password changed.</h3>
                <p className="text-ink-700 text-sm">Your password has been updated successfully.</p>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="p-6 lg:p-8 space-y-5">
                {passwordError && (
                  <div className="bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <p className="text-ember-deep text-sm">{passwordError}</p>
                  </div>
                )}

                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                    New password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={inputClass}
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    disabled={isChangingPassword}
                  />
                  <p className="text-xs text-ink-500 mt-1.5">Minimum 8 characters</p>
                </div>

                <div>
                  <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                    placeholder="Confirm new password"
                    required
                    disabled={isChangingPassword}
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    disabled={isChangingPassword}
                    className="flex-1 px-4 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="flex-1 px-4 py-2.5 bg-ember hover:bg-ember-deep disabled:opacity-50 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
                  >
                    {isChangingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        Changing...
                      </>
                    ) : (
                      'Change password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
